import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  MenuItem,
  Stack,
  Alert,
  CircularProgress,
} from '@mui/material';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { validationService } from '../../services/validationService';
import { TriggerValidationRequest, ValidationSource } from '../../types/validation.types';
import { usePagesList } from '../../hooks/usePagesList';
import { useSiteStore } from '../../store/site.store';

interface TriggerValidationDialogProps {
  open: boolean;
  onClose: () => void;
  siteId?: number;
  pageUrl?: string;
}

const TriggerValidationDialog: React.FC<TriggerValidationDialogProps> = ({
  open,
  onClose,
  siteId: initialSiteId,
  pageUrl: initialPageUrl,
}) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { selectedSite } = useSiteStore();

  const siteId = selectedSite?.id || initialSiteId || '';
  const [pageId, setPageId] = useState<number | ''>(initialPageUrl ? '' : '');
  const [source, setSource] = useState<ValidationSource>('LIVE');
  const [priority, setPriority] = useState<number>(5);
  const [error, setError] = useState<string | null>(null);

  // Fetch pages for selected site
  const { pages, loading: pagesLoading } = usePagesList();

  const triggerMutation = useMutation({
    mutationFn: (request: TriggerValidationRequest) => validationService.triggerValidation(request),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['allValidations'] });
      onClose();
      navigate(`/validation/runs/${data.id}`);
    },
    onError: (err: any) => {
      setError(err.response?.data?.message || 'Failed to trigger validation');
    },
  });

  const handleSubmit = () => {
    setError(null);

    // Basic validation
    if (!siteId || typeof siteId !== 'number') {
      setError('Site is required');
      return;
    }

    if (!pageId || typeof pageId !== 'number') {
      setError('Page is required');
      return;
    }

    const selectedPage = pages.find(p => p.id === pageId);
    if (!selectedPage) {
      setError('Selected page not found');
      return;
    }

    const request: TriggerValidationRequest = {
      pageUrl: selectedPage.url,
      siteId,
      source,
      priority,
    };

    triggerMutation.mutate(request);
  };

  const handleClose = () => {
    if (!triggerMutation.isPending) {
      setPageId('');
      setSource('LIVE');
      setPriority(5);
      setError(null);
      onClose();
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Trigger Validation</DialogTitle>
      <DialogContent>
        <Stack spacing={3} sx={{ mt: 1 }}>
          {error && (
            <Alert severity="error" onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          <TextField
            label="Site"
            value={selectedSite ? `${selectedSite.name} (${selectedSite.url})` : 'No site selected'}
            fullWidth
            disabled
            helperText="Current active site (non-editable)"
          />

          <TextField
            label="Page"
            value={pageId}
            onChange={(e) => setPageId(e.target.value ? Number(e.target.value) : '')}
            select
            fullWidth
            required
            disabled={triggerMutation.isPending || pagesLoading || !siteId}
            helperText={!siteId ? "Select a site first" : "Select the page to validate"}
          >
            {pagesLoading ? (
              <MenuItem disabled>Loading pages...</MenuItem>
            ) : !siteId ? (
              <MenuItem disabled>Please select a site</MenuItem>
            ) : pages && pages.length > 0 ? (
              pages.map((page) => (
                <MenuItem key={page.id} value={page.id}>
                  {page.url}
                </MenuItem>
              ))
            ) : (
              <MenuItem disabled>No pages available</MenuItem>
            )}
          </TextField>

          <TextField
            label="Source"
            value={source}
            onChange={(e) => setSource(e.target.value as ValidationSource)}
            select
            fullWidth
            disabled={triggerMutation.isPending}
            helperText="Choose whether to validate live page or latest snapshot"
          >
            <MenuItem value="LIVE">Live (fetch from web)</MenuItem>
            <MenuItem value="SNAPSHOT">Snapshot (use latest crawl data)</MenuItem>
          </TextField>

          <TextField
            label="Priority"
            value={priority}
            onChange={(e) => setPriority(parseInt(e.target.value, 10))}
            select
            fullWidth
            disabled={triggerMutation.isPending}
            helperText="Higher priority validations are processed first"
          >
            <MenuItem value={1}>1 - Highest</MenuItem>
            <MenuItem value={3}>3 - High</MenuItem>
            <MenuItem value={5}>5 - Normal</MenuItem>
            <MenuItem value={7}>7 - Low</MenuItem>
            <MenuItem value={9}>9 - Lowest</MenuItem>
          </TextField>

          {!siteId && (
            <Alert severity="warning">
              Site is required to trigger validation. Please select a site first.
            </Alert>
          )}
          {siteId && pages.length === 0 && !pagesLoading && (
            <Alert severity="info">
              No pages found for the selected site. Please add pages first.
            </Alert>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={triggerMutation.isPending}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={triggerMutation.isPending || !siteId || !pageId}
          startIcon={triggerMutation.isPending ? <CircularProgress size={20} /> : undefined}
        >
          {triggerMutation.isPending ? 'Triggering...' : 'Trigger Validation'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TriggerValidationDialog;
